import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Building2,
  Calendar,
  ChevronDown,
  FileText,
  Home,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Recycle,
  RefreshCw,
  Settings,
  Users,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/context/AuthContext'
import { useSync } from '@/hooks/useSync'
import { usePendingSyncCount } from '@/hooks/usePendingSyncCount'
import { SyncIndicator } from '@/components/shared/SyncIndicator'
import { Button } from '@/components/ui/button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import { Separator } from '@/components/ui/separator'
import type { UserRole } from '@/types'

interface NavItem {
  label: string
  to: string
  icon: React.ElementType
  roles: UserRole[]
  showPendingBadge?: boolean
}

interface NavGroup {
  label: string
  icon: React.ElementType
  roles: UserRole[]
  items: NavItem[]
}

interface NavbarProps {
  isMobileMenuOpen: boolean
  onOpenMobileMenu: () => void
  onCloseMobileMenu: () => void
}

const navItems: NavItem[] = [
  {
    label: 'Inicio',
    to: '/home',
    icon: Home,
    roles: ['admin', 'operator', 'client'],
  },
  {
    label: 'Dashboard',
    to: '/dashboard',
    icon: LayoutDashboard,
    roles: ['admin'],
  },
  {
    label: 'Agenda de retiros',
    to: '/pickups',
    icon: Calendar,
    roles: ['admin', 'operator'],
  },
  {
    label: 'Sincronización',
    to: '/sync',
    icon: RefreshCw,
    roles: ['admin', 'operator'],
    showPendingBadge: true,
  },
]

const adminGroup: NavGroup = {
  label: 'Administración',
  icon: Settings,
  roles: ['admin'],
  items: [
    {
      label: 'Usuarios',
      to: '/users',
      icon: Users,
      roles: ['admin'],
    },
    {
      label: 'Clientes',
      to: '/clients',
      icon: Building2,
      roles: ['admin'],
    },
    {
      label: 'Tipos de residuo',
      to: '/waste-types',
      icon: Recycle,
      roles: ['admin'],
    },
    {
      label: 'Disposiciones finales',
      to: '/destinations',
      icon: MapPin,
      roles: ['admin'],
    },
    {
      label: 'Informes ESG',
      to: '/reports/esg',
      icon: FileText,
      roles: ['admin'],
    },
  ],
}

function PendingBadge({ count }: { count: number }) {
  return (
    <span
      aria-label={`${count} borradores pendientes de sincronizar`}
      className="flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white"
    >
      {count > 9 ? '9+' : count}
    </span>
  )
}

export function Navbar({
  isMobileMenuOpen,
  onOpenMobileMenu,
  onCloseMobileMenu,
}: NavbarProps) {
  const { user, role, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { isOnline, ...syncState } = useSync()
  const pendingCount = usePendingSyncCount()
  const [desktopAdminMenuValue, setDesktopAdminMenuValue] = useState('')
  const [isMobileAdminMenuOpen, setIsMobileAdminMenuOpen] =
    useState<boolean>(false)

  useEffect(() => {
    if (!isMobileMenuOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isMobileMenuOpen])

  const handleLogout = async () => {
    await logout()
    onCloseMobileMenu()
    navigate('/login')
  }

  const visibleItems = navItems.filter(
    (item) => role && item.roles.includes(role)
  )
  const showAdminGroup = role ? adminGroup.roles.includes(role) : false
  const visibleAdminItems = useMemo(
    () => adminGroup.items.filter((item) => role && item.roles.includes(role)),
    [role]
  )
  const isAdminSectionActive = visibleAdminItems.some(({ to }) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`)
  )

  useEffect(() => {
    setDesktopAdminMenuValue('')
  }, [location.pathname])

  useEffect(() => {
    if (showAdminGroup && isAdminSectionActive) {
      setIsMobileAdminMenuOpen(true)
    }
  }, [isAdminSectionActive, showAdminGroup])

  const handleNavigate = () => {
    setDesktopAdminMenuValue('')
    setIsMobileAdminMenuOpen(false)
    onCloseMobileMenu()
  }

  return (
    <>
      <header className="sticky top-0 z-30 hidden h-16 items-center gap-4 border-b bg-white/95 px-6 backdrop-blur lg:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center overflow-hidden">
            <img src="/logo-landscape.png" alt="PuroViento" className="h-full w-full object-contain" />
          </div>
        </div>

        <nav className="flex flex-1 items-center gap-1">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/home'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      'h-4 w-4',
                      isActive ? 'text-secondary' : 'text-gray-400'
                    )}
                  />
                  {item.label}
                  {item.showPendingBadge && pendingCount > 0 && <PendingBadge count={pendingCount} />}
                </>
              )}
            </NavLink>
          ))}

          {showAdminGroup && (
            <NavigationMenu
              value={desktopAdminMenuValue}
              onValueChange={(value) => setDesktopAdminMenuValue(value || '')}
              className="flex-none"
            >
              <NavigationMenuList>
                <NavigationMenuItem value="administracion">
                  <NavigationMenuTrigger
                    className={cn(
                      'h-auto rounded-md bg-transparent px-3 py-2 text-sm font-medium text-gray-600 shadow-none hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground',
                      isAdminSectionActive &&
                        'bg-accent text-accent-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent'
                    )}
                  >
                    <adminGroup.icon
                      className={cn(
                        'h-4 w-4',
                        isAdminSectionActive
                          ? 'text-secondary'
                          : 'text-gray-400'
                      )}
                    />
                    {adminGroup.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="w-60">
                    <div className="space-y-1 p-1">
                      {visibleAdminItems.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.to === '/home'}
                          onClick={() => setDesktopAdminMenuValue('')}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-accent text-accent-foreground'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            )
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <item.icon
                                className={cn(
                                  'h-4 w-4',
                                  isActive ? 'text-secondary' : 'text-gray-400'
                                )}
                              />
                              {item.label}
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <SyncIndicator
            isOnline={isOnline}
            syncState={syncState}
            onSync={syncState.sync}
            compact
          />
          <Separator orientation="vertical" className="h-6" />
          <div className="text-right">
            <p className="text-sm font-medium leading-tight text-gray-900">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500">
              {role === 'admin'
                ? 'Administrador'
                : role === 'operator'
                  ? 'Operador'
                  : 'Cliente'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-gray-600 hover:bg-red-50 hover:text-red-600"
            onClick={handleLogout}
            aria-label="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-white/95 px-4 backdrop-blur lg:hidden">
        <div className="flex items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="PuroViento" className="h-full w-full object-contain" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">PuroViento</p>
            <p className="text-xs text-gray-500">
              {role === 'admin'
                ? 'Administrador'
                : role === 'operator'
                  ? 'Operador'
                  : 'Cliente'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SyncIndicator
            isOnline={isOnline}
            syncState={syncState}
            onSync={syncState.sync}
            compact
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={onOpenMobileMenu}
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {isMobileMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-gray-950/40 lg:hidden"
          onClick={onCloseMobileMenu}
          aria-label="Cerrar menú"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[86vw] max-w-[320px] flex-col border-r bg-white shadow-xl transition-transform lg:hidden',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between gap-2 px-5 py-2">
          <div className="flex items-center gap-2">
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="PuroViento" className="h-full w-full object-contain" />
            </div>
            <span className="text-lg font-bold text-gray-900">PuroViento</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={onCloseMobileMenu}
            aria-label="Cerrar menú"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <Separator />

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {visibleItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/home'}
                  onClick={handleNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className={cn(
                          'h-4 w-4',
                          isActive ? 'text-secondary' : 'text-gray-400'
                        )}
                      />
                      {item.label}
                      {item.showPendingBadge && pendingCount > 0 && <PendingBadge count={pendingCount} />}
                    </>
                  )}
                </NavLink>
              </li>
            ))}

            {showAdminGroup && (
              <li>
                <button
                  type="button"
                  onClick={() =>
                    setIsMobileAdminMenuOpen((current) => !current)
                  }
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors',
                    isAdminSectionActive || isMobileAdminMenuOpen
                      ? 'bg-accent text-accent-foreground'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                  aria-expanded={isMobileAdminMenuOpen}
                >
                  <adminGroup.icon
                    className={cn(
                      'h-4 w-4',
                      isAdminSectionActive || isMobileAdminMenuOpen
                        ? 'text-secondary'
                        : 'text-gray-400'
                    )}
                  />
                  <span className="flex-1 text-left">{adminGroup.label}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      isMobileAdminMenuOpen ? 'rotate-180' : ''
                    )}
                  />
                </button>

                {isMobileAdminMenuOpen && (
                  <div className="mt-1 space-y-1 pl-4">
                    {visibleAdminItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/home'}
                        onClick={handleNavigate}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-accent text-accent-foreground'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              className={cn(
                                'h-4 w-4',
                                isActive ? 'text-secondary' : 'text-gray-400'
                              )}
                            />
                            {item.label}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </li>
            )}
          </ul>

        </nav>

        <Separator />

        <div className="px-3 py-3">
          <SyncIndicator
            isOnline={isOnline}
            syncState={syncState}
            onSync={syncState.sync}
            compact={false}
          />
        </div>

        <Separator />

        <div className="p-3">
          <div className="mb-2 rounded-md bg-gray-50 px-3 py-2">
            <p className="truncate text-sm font-medium text-gray-900">
              {user?.name}
            </p>
            <p className="truncate text-xs text-gray-500">{user?.email}</p>
            <span className="mt-1 inline-block rounded-full bg-accent px-2 py-0.5 text-xs font-medium capitalize text-accent-foreground">
              {role}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-gray-600 hover:bg-red-50 hover:text-red-600"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar sesión
          </Button>
          <p className="mt-2 text-center text-xs text-gray-400">v{__APP_VERSION__}</p>
        </div>
      </aside>
    </>
  )
}
